import { getChatMailboxPollIntervalMs } from '@/components/pages/chat/utils/chatMailboxPoll'
import { debugMobileNav } from '@/common/debug/mobileNavDebug'
import { attachDocumentVisibilityPolling } from '@features/_common/documentVisibilityPolling'

export default {
    data() {
        return {
            mailboxNotificationCount: 0,
            mailboxSummaryPollIntervalId: null,
            mailboxTrackingActive: true,
            navigationUpdatesPaused: false,
            mailboxSummaryPollingPausedByVisibility: false,
            detachMailboxVisibilityPolling: null,
        }
    },

    mounted() {
        this._pauseNavigationUpdates = () => {
            this.navigationUpdatesPaused = true
            debugMobileNav('pause mailbox mixin updates', {
                component: this.$options?.name || 'unknown',
            })
        }
        this._resumeNavigationUpdates = () => {
            this.navigationUpdatesPaused = false
            debugMobileNav('resume mailbox mixin updates', {
                component: this.$options?.name || 'unknown',
            })
        }

        window.addEventListener('chat-mailbox-notifications-changed', this.onMailboxNotificationsChanged)
        window.addEventListener('etrainer-navigation-start', this._pauseNavigationUpdates)
        window.addEventListener('etrainer-navigation-end', this._resumeNavigationUpdates)
        this.loadMailboxNotificationCount()
        this.startMailboxSummaryPolling()
        this.detachMailboxVisibilityPolling = attachDocumentVisibilityPolling({
            pause: () => this.pauseMailboxSummaryPollingForHiddenDocument(),
            resume: () => this.resumeMailboxSummaryPollingForVisibleDocument(),
        })
    },

    beforeUnmount() {
        if (typeof this.detachMailboxVisibilityPolling === 'function') {
            this.detachMailboxVisibilityPolling()
            this.detachMailboxVisibilityPolling = null
        }
        this.stopMailboxSummaryPolling()
        this.mailboxTrackingActive = false
        window.removeEventListener('chat-mailbox-notifications-changed', this.onMailboxNotificationsChanged)
        window.removeEventListener('etrainer-navigation-start', this._pauseNavigationUpdates)
        window.removeEventListener('etrainer-navigation-end', this._resumeNavigationUpdates)
    },

    methods: {
        canTrackMailboxNotifications() {
            return true
        },

        isMailboxMixinActive() {
            return this.mailboxTrackingActive
                && !this.$.isUnmounted
                && !this.navigationUpdatesPaused
        },

        setMailboxNotificationCount(count) {
            if (!this.isMailboxMixinActive()) {
                debugMobileNav('skip mailbox count update', {
                    component: this.$options?.name || 'unknown',
                    count,
                    trackingActive: this.mailboxTrackingActive,
                    navigationPaused: this.navigationUpdatesPaused,
                    isUnmounted: this.$.isUnmounted,
                })
                return
            }

            this.mailboxNotificationCount = Math.max(0, count)
        },

        onMailboxNotificationsChanged(event) {
            const count = Number(event?.detail?.count)
            if (!Number.isFinite(count)) {
                return
            }

            this.setMailboxNotificationCount(count)
        },

        async loadMailboxNotificationCount() {
            if (!this.isMailboxMixinActive() || !this.canTrackMailboxNotifications() || !window.axios) {
                if (this.isMailboxMixinActive()) {
                    this.setMailboxNotificationCount(0)
                }
                return
            }

            try {
                const response = await window.axios.get('/chat/private/summary')
                if (!this.isMailboxMixinActive()) {
                    return
                }

                const count = Number(response?.data?.mailbox_notification_count)
                this.setMailboxNotificationCount(Number.isFinite(count) ? count : 0)
            } catch (error) {
                this.setMailboxNotificationCount(0)
            }
        },

        pauseMailboxSummaryPollingForHiddenDocument() {
            this.mailboxSummaryPollingPausedByVisibility = true
            this.stopMailboxSummaryPolling()
        },

        resumeMailboxSummaryPollingForVisibleDocument() {
            if (!this.isMailboxMixinActive() || !this.canTrackMailboxNotifications()) {
                return
            }

            this.mailboxSummaryPollingPausedByVisibility = false
            this.loadMailboxNotificationCount()
            this.startMailboxSummaryPolling()
        },

        startMailboxSummaryPolling() {
            this.stopMailboxSummaryPolling()
            if (
                this.mailboxSummaryPollingPausedByVisibility
                || !this.isMailboxMixinActive()
                || !this.canTrackMailboxNotifications()
            ) {
                return
            }

            this.mailboxSummaryPollIntervalId = window.setInterval(() => {
                this.loadMailboxNotificationCount()
            }, getChatMailboxPollIntervalMs())
        },

        stopMailboxSummaryPolling() {
            if (this.mailboxSummaryPollIntervalId) {
                window.clearInterval(this.mailboxSummaryPollIntervalId)
                this.mailboxSummaryPollIntervalId = null
            }
        },
    },
}
